import { LoadingButton } from "@mui/lab";
import { Tooltip } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { AnimatePresence, motion } from "motion/react";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { TbCancel, TbInfoCircle, TbProgressCheck, TbTableRow } from "react-icons/tb";

import { useClinicUnassignedSaveAction } from "@/hooks/useClinicUnassignedSaveAction";
import {
    useClinicUnassignedChanges,
    useClinicUnassignedPassengersTableActions,
} from "@/stores/use-clinic-arrival-unassigned-passengers-table";

type ClinicUnassignedChangesButtonProps = {
    apiRef: RefObject<GridApiPremium | null>;
};

function ClinicUnassignedChangesButton({ apiRef }: ClinicUnassignedChangesButtonProps) {
    const { t } = useTranslation();
    const changes = useClinicUnassignedChanges();
    const { handleSaveChanges, isPending, saveDisabled, saveDisabledReason, totals } =
        useClinicUnassignedSaveAction(apiRef);
    const { handleRowModesModelCancel } = useClinicUnassignedPassengersTableActions();

    const handleCancelChanges = () => {
        if (isPending) return;
        const grid = apiRef.current;
        if (!grid) return;
        for (const id of Object.keys(changes)) {
            void handleRowModesModelCancel(id, grid, true, "userId");
        }
    };

    const saveButtonTooltip =
        saveDisabled && saveDisabledReason
            ? saveDisabledReason
            : !saveDisabled && totals.row > 0
              ? t("clinicArrival.saveShortcutHint")
              : "";

    return (
        <div className="flex  items-end gap-1">
            <AnimatePresence initial={false}>
                {(totals.row > 0 || isPending) && (
                    <div className="flex items-end gap-1">
                        <motion.div
                            key="save-btn"
                            initial={{ y: -100, opacity: 0 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ y: 20, opacity: 0 }}
                            className="shrink-0 grow-0"
                        >
                            <Tooltip title={saveButtonTooltip}>
                                <span>
                                    <LoadingButton
                                        className="max-w-fit"
                                        variant="contained"
                                        color="primary"
                                        disabled={saveDisabled}
                                        loading={isPending}
                                        fullWidth
                                        onClick={handleSaveChanges}
                                        startIcon={<TbProgressCheck />}
                                    >
                                        {t("buttons.save")}
                                    </LoadingButton>
                                </span>
                            </Tooltip>
                        </motion.div>
                        <motion.div
                            key="cancel-btn"
                            initial={{ x: 100, opacity: 0 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ y: 20, opacity: 0 }}
                            className="shrink-0 grow-0"
                        >
                            <LoadingButton
                                className="max-w-fit"
                                variant="outlined"
                                color="secondary"
                                disabled={totals.row === 0 || isPending}
                                fullWidth
                                onClick={handleCancelChanges}
                                startIcon={<TbCancel />}
                            >
                                {t("buttons.cancel")}
                            </LoadingButton>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            <div className="flex min-h-[37px] shrink-0 grow-0 items-center gap-1 rounded bg-gray-100 px-2 py-1">
                <TbTableRow className="translate-y-[1px]" /> {totals.row}
                <Tooltip
                    title={
                        <div className="flex flex-col gap-1">
                            <div>{t("changeRowCount") + " " + totals.row}</div>
                            <div>{t("changeCellCount") + " " + totals.cell}</div>
                        </div>
                    }
                >
                    <span className="!self-start !translate-x-1">
                        <TbInfoCircle size={12} />
                    </span>
                </Tooltip>
            </div>
        </div>
    );
}

export default ClinicUnassignedChangesButton;
